import type { IncidentReport } from 'components/CriticalIncident/interfaces';
import { type SetupWorker } from 'msw/browser';
import { isMockServerRunning } from 'utils/isMockServerRunning';
import { extendWindowTph } from 'utils/windowTph';
import { notifyMockDbUpdated } from './mockDbSync';

const REFERRAL_COUNT_RANGE = 15;
const CASE_COUNT_RANGE = 10;
const DOCUMENT_REQUEST_COUNT_RANGE = 15;

async function seedMockData() {
    const { default: CustomDB } = await import('../../mock-utils/custom-db.mjs');
    const { createSampleReferrals, createSampleDocumentRequests, createSampleCICases, createSampleIncidentReports } = await import('../../mock-utils/generate-data/index.mjs');

    const referralsDb = new CustomDB('referrals');
    if (!(await referralsDb.findAll()).length) {
        await createSampleReferrals(Math.ceil(Math.random() * REFERRAL_COUNT_RANGE), '');
    }

    const ciCasesDb = new CustomDB('ciCases');
    if (!(await ciCasesDb.findAll()).length) {
        await createSampleCICases(Math.ceil(Math.random() * CASE_COUNT_RANGE), '');
    }

    // Must run after CI cases are seeded — one incident report is generated for every
    // CI case above with status 'P' or 'S' (never for 'O'), drawing its caseId/activityId
    // from that case.
    const incidentReportsDb = new CustomDB('incidentReports');
    if (!(await incidentReportsDb.findAll()).length) {
        await createSampleIncidentReports('');
    }

    const documentRequestsDb = new CustomDB('documentRequests');
    if (!(await documentRequestsDb.findAll()).length) {
        await createSampleDocumentRequests(Math.ceil(Math.random() * DOCUMENT_REQUEST_COUNT_RANGE), '');
    }
}

let worker: SetupWorker | null = null;

/**
 * Unregisters any service worker at this origin that isn't `mockServiceWorker.js`
 * (e.g. a stale `sw.js` kill-switch registration left over on some browsers, see
 * TPH-1665) so it can't contest the root scope with MSW's own worker and force
 * an unexpected reload.
 */
async function evictForeignServiceWorkers() {
    if (!('serviceWorker' in navigator)) {
        return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
        registrations
            .filter((registration) => {
                const scriptURL = registration.active?.scriptURL
                    ?? registration.installing?.scriptURL
                    ?? registration.waiting?.scriptURL;
                return scriptURL && !scriptURL.endsWith('/mockServiceWorker.js');
            })
            .map((registration) => registration.unregister()),
    );
}

/**
 * Starts the MSW Service Worker if it isn't already running. Exposed via
 * `window.TPH.startMSW()` (see the `window.TPH` extension below) so the
 * worker can be started on demand without a page reload.
 */
export async function startMSW() {
    if (worker) {
        return;
    }

    await evictForeignServiceWorkers();

    const { setupWorker } = await import('msw/browser');
    const { default: handlers } = await import('../../mock-utils/msw-handlers.mjs');

    worker = setupWorker(...handlers);
    await worker.start({
        onUnhandledRequest: 'bypass',
        serviceWorker: { url: '/mockServiceWorker.js' },
    });

    await seedMockData();
}

/**
 * Stops the MSW Service Worker if it's running. Exposed via
 * `window.TPH.stopMSW()` (see the `window.TPH` extension below). Toggling the
 * `use-mock-server` flag off does NOT stop the worker on its own — it must be
 * stopped explicitly so in-flight mock state isn't torn down unexpectedly.
 */
export async function stopMSW(clearDB = false) {
    if (worker) {
        worker.stop();
        worker = null;
    }

    if (clearDB) {
        const { clearAllCollections } = await import('../../mock-utils/custom-db.mjs');
        await clearAllCollections();
    }
}

/**
 * Starts the MSW Service Worker so a deployed static build can run in mock
 * mode. Never activates when the standalone mock server (`node
 * mock-server.mjs`) is already running and reachable.
 */
export async function enableMockServiceWorker() {
    if (await isMockServerRunning()) {
        return;
    }

    await startMSW();
}

/**
 * Inserts or updates a record in a mock IndexedDB collection. Exposed via
 * `window.TPH.insertMockRecord(collectionName, record)` for seeding ad-hoc
 * data from the browser console. `record` may be a JSON string or a plain
 * object, and may be partial — any field the collection's `createSingle*`
 * generator (see `mock-utils/generate-data/`) knows how to fake is backfilled
 * with realistic data, and the caller's own fields take precedence over the
 * generated ones. Collections without a generator (e.g. `credentials`) skip
 * backfill and use the record as-is. Malformed JSON is rejected with a
 * descriptive error rather than throwing a raw `SyntaxError`. Whether the
 * record is an update or a new insert is decided by the caller's own input,
 * before generator backfill runs — passing an id updates that existing
 * record, otherwise a new one is inserted (backfilling an id if the
 * collection's generator or `crypto.randomUUID()` didn't already supply one).
 */
export async function insertMockRecord(collectionName: string, record: string | Record<string, unknown>) {
    const { default: CustomDB, COLLECTION_NAMES } = await import('../../mock-utils/custom-db.mjs');

    if (!COLLECTION_NAMES.includes(collectionName)) {
        throw new Error(`Unknown mock collection "${collectionName}". Valid collections: ${COLLECTION_NAMES.join(', ')}`);
    }

    let data: Record<string, unknown>;
    if (typeof record === 'string') {
        try {
            data = JSON.parse(record);
        } catch (error) {
            throw new Error(`Malformed JSON provided for "${collectionName}" record: ${(error as Error).message}`);
        }
    } else {
        data = record;
    }

    if (data === null || typeof data !== 'object' || Array.isArray(data)) {
        throw new Error(`Record for "${collectionName}" must be a JSON object.`);
    }

    const db = new CustomDB(collectionName);
    const callerProvidedId = data[db.idFieldName];

    const { createSingleReferral, createSingleDocumentRequest, createSingleIncidentReport, createSingleCICase, getRandomFaker } =
        await import('../../mock-utils/generate-data/index.mjs');
    const recordGenerators: Record<string, (params: any) => any> = {
        referrals: createSingleReferral,
        documentRequests: createSingleDocumentRequest,
        incidentReports: createSingleIncidentReport,
        ciCases: createSingleCICase,
    };

    const generateRecord = recordGenerators[collectionName];
    if (generateRecord) {
        const faker = await getRandomFaker();
        data = { ...generateRecord({ faker }), ...data };
    }

    // Only treat this as an update when the caller explicitly supplied an id;
    // otherwise always insert, even if a collection generator backfilled its
    // own (freshly generated, not-yet-persisted) id above.
    let result: unknown;
    if (!callerProvidedId) {
        data[db.idFieldName] = data[db.idFieldName] || crypto.randomUUID();
        result = await db.insert(data);
    } else {
        result = await db.updateById(callerProvidedId, data);
    }

    notifyMockDbUpdated(collectionName);
    return result;
}

/**
 * updates a single incidentReport identified by its activityId
 *
 * @param {number|string} activityId - activityId of the incident report to update
 * @param {Partial<IncidentReport>} updatedFields - the fields to update in the report
*/
export async function updateMockIncidentReport(activityId: number | string, updatedFields: Partial<IncidentReport>) {
    const { default: CustomDB } = await import('../../mock-utils/custom-db.mjs');
    const db = new CustomDB('incidentReports');
    const existingReport = await db.findByField('activityId', Number(activityId));
    if (!existingReport) {
        updatedFields.id = crypto.randomUUID();
    }
    const updatedReport = { ...existingReport, ...updatedFields };
    const result = await db.updateById(updatedReport?.id || updatedReport.id, updatedReport);
    notifyMockDbUpdated('incidentReports');
    return result;
}

/**
 * inserts a single incidentReport for the given case/activity
 *
 * @param {number|string} caseId - caseId of the CI case that owns the report
 * @param {number|string} activityId - activityId of the CI case that owns the report
 * @param {boolean} overwrite - forces the replacement of an existing report
*/
export async function insertMockIncidentReport(caseId: number | string, activityId: number | string, overwrite: boolean) {
    const { default: CustomDB } = await import('../../mock-utils/custom-db.mjs');
    const db = new CustomDB('incidentReports');
    const numericCaseId = Number(caseId);
    const numericActivityId = Number(activityId);
    const existingReport = await db.findByField('activityId', numericActivityId);
    if (existingReport && !overwrite) {
        console.warn('⚠️ Found an existing report for this case. If you want to replace this report, add a third boolean parameter: TPH.insertMockIncidentReport(caseId, activityId, true)', existingReport);
        return existingReport;
    }
    if (existingReport && overwrite) {
        await db.deleteById(existingReport.id);
    }
    const result = await insertMockRecord('incidentReports', { caseId: numericCaseId, activityId: numericActivityId });
    return result;
}

/**
 * Exposes `startMSW`/`stopMSW`/`insertMockRecord`/`updateMockIncidentReport`/`insertMockIncidentReport` on `window.TPH`
 */
extendWindowTph(base => ({ ...base, startMSW, stopMSW, insertMockRecord, updateMockIncidentReport, insertMockIncidentReport }));
